import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { usePaywall } from "../components/PaywallModal";
import { handlePaywallResponse } from "../utils/paywallHelper";
import ReactMarkdown from "react-markdown";
import {
  FileText,
  Mail,
  Copy,
  Download,
  Loader2,
  Briefcase,
  Sparkles,
  ChevronDown,
  RefreshCw,
  Check
} from "lucide-react";

const TONES = [
  { value: "professional", label: "Professional" },
  { value: "creative", label: "Creative" },
  { value: "executive", label: "Executive" },
];

const CVBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showPaywall } = usePaywall();
  const { jobId } = useParams();

  const [activeTab, setActiveTab] = useState("cv");
  const [loading, setLoading] = useState(false);
  const [tone, setTone] = useState("professional");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(jobId || "");
  const [customJob, setCustomJob] = useState({ title: "", company: "" });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/jobs?limit=50`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setJobs(Array.isArray(data) ? data : data.jobs || []);
      }
    } catch {}
  };

  const selectedJobData = jobs.find((j) => j.job_id === selectedJob);

  const handleGenerate = async () => {
    setLoading(true);
    setResult(null);
    setCopied(false);

    const token = localStorage.getItem("session_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const endpoint = activeTab === "cv" ? "/ai/generate-cv" : "/ai/generate-cover-letter";
    const body = {
      tone,
      job_id: selectedJob || undefined,
      job_title: selectedJobData?.title || customJob.title || "",
      company_name: selectedJobData?.company_name || customJob.company || "",
    };

    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(activeTab === "cv" ? data.cv_content : data.cover_letter);
        toast.success(`${activeTab === "cv" ? "CV" : "Cover Letter"} generated!`);
      } else {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;

        // Check for paywall response (402/403)
        if (detail && typeof detail === "object" && detail.paywall && showPaywall) {
          showPaywall(detail);
          return;
        }

        // Check for profile completion needed
        if (detail && typeof detail === "object" && detail.action === "complete_profile") {
          toast.error("Please complete your profile before generating. Go to My Profile to add your details.");
          return;
        }

        // Show the backend error message
        const msg = typeof detail === "string" ? detail : (detail?.message || "Generation failed");
        toast.error(msg);
      }
    } catch (e) {
      console.error("CV generation error:", e);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([result], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = activeTab === "cv" ? "my-cv.md" : "cover-letter.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="cv-builder-title">
              <Sparkles className="w-7 h-7 text-crimson" />
              AI CV & Cover Letter Builder
            </h1>
            <p className="text-gray-400 mt-1 text-sm">Generate tailored CVs and cover letters powered by AI</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-obsidian-light border border-[#222] rounded-lg p-1 w-fit">
            {[
              { id: "cv", label: "CV / Resume", icon: FileText },
              { id: "cover_letter", label: "Cover Letter", icon: Mail },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setResult(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
                  activeTab === tab.id ? "bg-crimson text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Options */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-5 space-y-4">
                <h3 className="font-semibold text-sm text-gray-300">Target Job (optional)</h3>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Select from jobs</label>
                  <select
                    value={selectedJob}
                    onChange={(e) => setSelectedJob(e.target.value)}
                    className={inputClass}
                    data-testid="select-job"
                  >
                    <option value="">No specific job</option>
                    {jobs.map((j) => (
                      <option key={j.job_id} value={j.job_id}>
                        {j.title} - {j.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                {!selectedJob && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Or enter manually</label>
                      <input
                        type="text"
                        placeholder="Job title (e.g., Software Engineer)"
                        value={customJob.title}
                        onChange={(e) => setCustomJob({ ...customJob, title: e.target.value })}
                        className={inputClass}
                        data-testid="custom-job-title"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Company name"
                      value={customJob.company}
                      onChange={(e) => setCustomJob({ ...customJob, company: e.target.value })}
                      className={inputClass}
                      data-testid="custom-company"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tone</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className={inputClass}
                    data-testid="tone-select"
                  >
                    {TONES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full bg-crimson hover:bg-crimson/90 text-white"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate {activeTab === "cv" ? "CV" : "Cover Letter"}</>
                  )}
                </Button>
              </div>
            </div>

            {/* Right: Result */}
            <div className="lg:col-span-3">
              <div className="bg-obsidian-light border border-[#222] rounded-lg min-h-[400px]">
                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                    {activeTab === "cv" ? <FileText className="w-12 h-12 mb-3 opacity-30" /> : <Mail className="w-12 h-12 mb-3 opacity-30" />}
                    <p className="text-sm">Your {activeTab === "cv" ? "CV" : "cover letter"} will appear here</p>
                    <p className="text-xs text-gray-600 mt-1">Click Generate to start</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center h-[400px]">
                    <Loader2 className="w-8 h-8 animate-spin text-crimson mb-3" />
                    <p className="text-sm text-gray-400">AI is crafting your {activeTab === "cv" ? "CV" : "cover letter"}...</p>
                  </div>
                )}

                {result && (
                  <div>
                    <div className="flex items-center justify-between border-b border-[#222] px-4 py-3">
                      <h3 className="font-semibold text-sm">Generated {activeTab === "cv" ? "CV" : "Cover Letter"}</h3>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handleCopy} className="border-[#333] text-gray-300 hover:bg-white/5 text-xs" data-testid="copy-btn">
                          {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                          {copied ? "Copied" : "Copy"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleDownload} className="border-[#333] text-gray-300 hover:bg-white/5 text-xs" data-testid="download-btn">
                          <Download className="w-3.5 h-3.5 mr-1" /> Download
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleGenerate} disabled={loading} className="border-[#333] text-gray-300 hover:bg-white/5 text-xs" data-testid="regenerate-btn">
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate
                        </Button>
                      </div>
                    </div>
                    <div className="p-5 prose prose-invert prose-sm max-w-none" data-testid="result-content">
                      <ReactMarkdown>{result}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CVBuilder;
